import React, { useState } from "react";
import { Phone, Mail, Facebook, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { PageHero } from "@/components/Decor";
import { CONTACT } from "@/lib/data";
import api from "@/lib/api";

export default function ContactPage() {
  const [form, setForm] = useState({ nom: "", email: "", telephone: "", sujet: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom || !form.email || !form.sujet || !form.message) {
      toast.error("Veuillez remplir tous les champs obligatoires.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message envoyé ! Nous vous répondrons rapidement.");
      setForm({ nom: "", email: "", telephone: "", sujet: "", message: "" });
    } catch (err) {
      toast.error("Échec de l'envoi. Réessayez ou appelez-nous directement.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Contact"
        title={<>Une question ? <em className="italic text-coral">Parlons-en</em></>}
        subtitle="Notre équipe est disponible pour répondre à toutes vos questions et organiser une visite du centre."
      />

      <section className="relative pb-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-8">
          {/* Info */}
          <div className="lg:col-span-2 space-y-4">
            <ContactCard
              icon={Phone}
              title="Téléphone"
              value={CONTACT.phone}
              href={`tel:${CONTACT.phoneRaw}`}
              accent
              testid="contact-phone"
            />
            <ContactCard
              icon={Mail}
              title="Email"
              value={CONTACT.email}
              href={`mailto:${CONTACT.email}`}
              testid="contact-email"
            />
            <ContactCard
              icon={Facebook}
              title="Facebook"
              value={CONTACT.facebookLabel}
              href={CONTACT.facebook}
              external
              testid="contact-facebook"
            />
            <ContactCard
              icon={MapPin}
              title="Localisation"
              value={CONTACT.adresse}
              testid="contact-address"
            />
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-7 sm:p-10 border border-ocean/5 shadow-xl" data-testid="contact-form">
              <h3 className="font-display text-2xl text-ocean mb-6">Envoyez-nous un message</h3>
              <div className="grid sm:grid-cols-2 gap-5">
                <FormField label="Nom complet *" value={form.nom} onChange={(v) => update("nom", v)} testid="contact-input-nom" />
                <FormField label="Email *" type="email" value={form.email} onChange={(v) => update("email", v)} testid="contact-input-email" />
                <FormField label="Téléphone" value={form.telephone} onChange={(v) => update("telephone", v)} testid="contact-input-telephone" />
                <FormField label="Sujet *" value={form.sujet} onChange={(v) => update("sujet", v)} testid="contact-input-sujet" />
                <div className="sm:col-span-2">
                  <label className="block text-xs uppercase tracking-wider text-ocean/55 font-semibold mb-2">Message *</label>
                  <textarea
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    rows={5}
                    data-testid="contact-input-message"
                    className="w-full px-4 py-3 rounded-xl border border-ocean/10 bg-sand/50 focus:bg-white focus:border-coral focus:ring-2 focus:ring-coral/20 transition-all text-sm text-ocean resize-none"
                  />
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
                {sent && <span className="text-green-600 text-sm font-medium">Votre message a bien été envoyé.</span>}
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit"
                  className="btn-pill btn-accent ml-auto disabled:opacity-60"
                >
                  {sending ? "Envoi..." : "Envoyer"} <Send size={16} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}

function ContactCard({ icon: Icon, title, value, href, external, accent, testid }) {
  const inner = (
    <div className={`rounded-3xl p-6 border transition-all ${accent ? "bg-coral text-white border-coral" : "bg-white border-ocean/5 hover:border-coral/40"}`}>
      <div className={`w-11 h-11 rounded-2xl flex items-center justify-center mb-3 ${accent ? "bg-white/20 text-white" : "bg-coral/10 text-coral"}`}>
        <Icon size={20} />
      </div>
      <div className={`text-xs uppercase tracking-widest font-semibold mb-1 ${accent ? "text-white/80" : "text-ocean/55"}`}>{title}</div>
      <div className={`font-display text-lg ${accent ? "text-white" : "text-ocean"} break-words`}>{value}</div>
    </div>
  );
  if (href) {
    return (
      <a href={href} target={external ? "_blank" : undefined} rel={external ? "noreferrer" : undefined} data-testid={testid} className="block">
        {inner}
      </a>
    );
  }
  return <div data-testid={testid}>{inner}</div>;
}

function FormField({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-ocean/55 font-semibold mb-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full px-4 py-3 rounded-xl border border-ocean/10 bg-sand/50 focus:bg-white focus:border-coral focus:ring-2 focus:ring-coral/20 transition-all text-sm text-ocean"
      />
    </div>
  );
}
